import { NavigationType, useLocation, type createBrowserRouter, type Location } from "react-router";

/**
 * Where a page was opened from (015 `contracts/screens.md` § Top bar, the block of 2026-10-07, from the
 * owner's review with Mejd of 2026-10-06: going back had no control on some pages and landed on the
 * wrong page on others). Every entry of the browser's history the application pushes carries, in the
 * entry's own `history.state` beside the router's fields, the page it was opened from: the entry
 * before it when the address's path changed, or, for a step inside one page (a peek, a filter, a tab
 * the address names), the origin of that page with one more step to walk back. The browser keeps the
 * record with its entry, so a reload, Back and Forward read the same record, and an entry the
 * browser drops takes its record with it.
 *
 * The top bar's Back walks the history back to that entry (`returnTo`), so it lands where the person
 * was, with that page's filters, date and scroll, and leaves the browser's Back and Forward agreeing
 * with it. A page opened by a direct link, or from outside the application, has no origin, and its
 * Back goes to the page's logical parent instead (`TopBar.tsx`).
 *
 * A replacement keeps the replaced entry's origin, since a redirect or a rewritten address is the same
 * visit, with one exception: a step inside one page that becomes another page in place (the side
 * peek's task opened as its full page) was opened from the page under the step, so its origin is the
 * entry before the step. A replacement that leaves the page while a step is open (a meeting deleted
 * with its peek open) therefore points back at the page left, as the browser's Back does.
 */

type Router = ReturnType<typeof createBrowserRouter>;

/** The entry a page was opened from, and how many entries back it lies. */
export interface Origin {
  /** The address, path, query and hash, as it stood when the person left it. */
  readonly href: string;
  readonly pathname: string;
  /** The router's key of the entry. */
  readonly key: string;
  /** The screen's name as its route metadata set it (`routes/meta.ts`), or null when it had none yet. */
  readonly title: string | null;
  /** How many entries back the origin lies: 1 from the page itself, one more for each step inside the page. */
  readonly depth: number;
}

/** What one history entry carries. */
interface EntryRecord {
  /** The screen's own name, which a page opened from this entry names its Back after. */
  readonly title: string | null;
  /** The page this entry's page was opened from. */
  readonly origin: Origin | null;
  /** The entry immediately before this one, when the application pushed this one. */
  readonly previous: Origin | null;
}

/** The field of `history.state` the record lives in, beside the router's own `usr`, `key` and `idx`. */
const FIELD = "batonEntry";
const EMPTY: EntryRecord = { title: null, origin: null, previous: null };

interface Current {
  readonly key: string;
  readonly href: string;
  readonly pathname: string;
  readonly record: EntryRecord;
}

let router: Router | null = null;
let current: Current | null = null;
/** A query change to make on the origin once the walk back reaches it (`returnTo`'s `search`). */
let pending: { readonly key: string; readonly search: (search: URLSearchParams) => URLSearchParams } | null = null;

function hrefOf(location: Pick<Location, "pathname" | "search" | "hash">): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

function isOrigin(value: unknown): value is Origin {
  if (typeof value !== "object" || value === null) return false;
  const origin = value as Record<string, unknown>;
  return typeof origin.href === "string" && typeof origin.pathname === "string" && typeof origin.key === "string" && (origin.title === null || typeof origin.title === "string") && typeof origin.depth === "number" && Number.isInteger(origin.depth) && origin.depth >= 1;
}

/** The record the browser holds for the entry on screen, read defensively since `history.state` is anyone's to write. */
function readEntry(key: string): EntryRecord | null {
  const state: unknown = window.history.state;
  if (typeof state !== "object" || state === null) return null;
  const fields = state as Record<string, unknown>;
  // The router names the entry it found with no key of its own "default" (`createBrowserHistory`).
  if ((typeof fields.key === "string" ? fields.key : "default") !== key) return null;
  const record = fields[FIELD];
  if (typeof record !== "object" || record === null) return null;
  const { title, origin, previous } = record as Record<string, unknown>;
  if (!(title === null || typeof title === "string")) return null;
  if (!(origin === null || isOrigin(origin)) || !(previous === null || isOrigin(previous))) return null;
  return { title, origin, previous };
}

function writeEntry(key: string, record: EntryRecord): void {
  const state: unknown = window.history.state;
  const fields = typeof state === "object" && state !== null ? (state as Record<string, unknown>) : {};
  if ((typeof fields.key === "string" ? fields.key : "default") !== key) return;
  window.history.replaceState({ ...fields, [FIELD]: record }, "");
}

/** The record of the entry the router has just moved to. */
function recordFor(action: NavigationType, from: Current, to: Location): EntryRecord {
  if (action === NavigationType.Pop) return readEntry(to.key) ?? EMPTY;
  if (action === NavigationType.Push) {
    const step: Origin = { href: from.href, pathname: from.pathname, key: from.key, title: from.record.title, depth: 1 };
    if (to.pathname !== from.pathname) return { title: null, origin: step, previous: step };
    const origin = from.record.origin === null ? null : { ...from.record.origin, depth: from.record.origin.depth + 1 };
    return { title: from.record.title, origin, previous: step };
  }
  if (to.pathname === from.pathname) return from.record;
  // A step inside a page that became another page in place was opened from the page under the step.
  const stepped = from.record.previous !== null && from.record.previous.pathname === from.pathname;
  if (stepped) return { title: null, origin: from.record.previous, previous: from.record.previous };
  return { title: null, origin: from.record.origin, previous: from.record.previous };
}

/** Starts recording the origins of the router's entries; `routes.tsx` calls it once for the router it creates. */
export function trackOrigins(created: Router): void {
  router = created;
  const at = created.state.location;
  current = { key: at.key, href: hrefOf(at), pathname: at.pathname, record: readEntry(at.key) ?? EMPTY };
  created.subscribe((state) => {
    const to = state.location;
    const from = current;
    if (from === null || to.key === from.key) return;
    const record = recordFor(state.historyAction, from, to);
    current = { key: to.key, href: hrefOf(to), pathname: to.pathname, record };
    if (state.historyAction !== NavigationType.Pop) writeEntry(to.key, record);
    const wanted = pending;
    pending = null;
    if (wanted !== null && state.historyAction === NavigationType.Pop && to.key === wanted.key) {
      const search = wanted.search(new URLSearchParams(to.search)).toString();
      void created.navigate({ pathname: to.pathname, search: search.length > 0 ? `?${search}` : "", hash: to.hash }, { replace: true, state: to.state });
    }
  });
}

/** Names the screen on the entry on screen, for the Back of a page opened from it; `useRouteMeta` calls it as the screen's name settles. */
export function noteScreenTitle(key: string, title: string): void {
  if (current?.key !== key || current.record.title === title) return;
  current = { ...current, record: { ...current.record, title } };
  writeEntry(key, current.record);
  for (const listener of titleListeners) listener();
}

const titleListeners = new Set<() => void>();

/** The name of the screen on screen as its own read settled it, or null before it has (019's page context chip names the page from it, 2026-10-15). */
export function currentScreenTitle(): string | null {
  return current?.record.title ?? null;
}

/** Hear every change of the screen's name, for a reader outside the screen. */
export function subscribeScreenTitle(listener: () => void): () => void {
  titleListeners.add(listener);
  return () => {
    titleListeners.delete(listener);
  };
}

/** The page the page on screen was opened from, or null when it was opened by a direct link or from outside the application. */
export function useOrigin(): Origin | null {
  const { key } = useLocation();
  return current !== null && current.key === key ? current.record.origin : null;
}

/**
 * Walks the history back to the origin, as the browser's Back would step by step, so Forward returns
 * to the page left. `search` changes the origin's query once it is back on screen, in place, which is
 * how the task's full page hands its task to the page it came from in a peek or a modal.
 */
export function returnTo(origin: Origin, search?: (search: URLSearchParams) => URLSearchParams): void {
  if (router === null) throw new Error("returnTo needs the router that recorded the origin");
  pending = search === undefined ? null : { key: origin.key, search };
  void router.navigate(-origin.depth);
}
